import React, { useCallback, useEffect, useRef, useState } from "react";
import CreateInvoiceQpay from "../../functions/invoice/createInvoiceQpay";
import { useUser } from "../../screens/user/context.js/userContext";
import { Button, Skeleton } from "antd";

const POPULAR_BANK_NAMES = [
  "Khan bank",
  "State bank 3.0",
  "Xac bank",
  "Trade and Development bank",
  "Capitron bank",
  "Social Pay",
  "Bogd bank",
  "M bank",
  "Monpay",
];

const popularRank = (name) => {
  const index = POPULAR_BANK_NAMES.findIndex(
    (label) => String(label).toLowerCase() === String(name || "").toLowerCase()
  );
  return index === -1 ? 999 : index;
};

const QpayBankList = ({ invoiceId }) => {
  const [banks, setBanks] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setPaying } = useUser();
  const fetchStartedRef = useRef(false);

  const fetchQpayData = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setLoadError(false);
    fetchStartedRef.current = true;
    setPaying("Төлбөрийн мэдээлэл татаж байна...");
    try {
      const response = await CreateInvoiceQpay(invoiceId);
      setPaying(null);
      if (!response?.data?.data?.urls) {
        setLoadError(true);
        setBanks([]);
        return;
      }
      const urls = [...response.data.data.urls].sort(
        (a, b) => popularRank(a.name) - popularRank(b.name)
      );
      setBanks(urls);
    } catch {
      setPaying(null);
      setLoadError(true);
      setBanks([]);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, setPaying]);

  useEffect(() => {
    if (invoiceId && !fetchStartedRef.current) {
      fetchQpayData();
    }
  }, [invoiceId, fetchQpayData]);

  const navigate = (link) => {
    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "link",
          url: link,
        })
      );
    } catch {
      window.location.href = link;
    }
  };

  if (loading && banks === null) {
    return (
      <div className="mt-2">
        <p className="mb-3 text-sm font-medium text-slate-700">
          Банкны аппликейшнээр төлөх
        </p>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col items-center rounded-xl border border-slate-100 bg-white p-3"
            >
              <Skeleton.Avatar active size={48} shape="square" />
              <Skeleton.Input active size="small" className="mt-2 !w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mt-2 rounded-xl border border-red-100 bg-red-50 p-4 text-center">
        <p className="mb-3 text-sm text-slate-700">
          Төлбөрийн холбоос авахад алдаа гарлаа
        </p>
        <Button
          type="primary"
          onClick={() => {
            fetchStartedRef.current = false;
            setBanks(null);
            fetchQpayData();
          }}
          className="!bg-smartTeal"
        >
          Дахин оролдох
        </Button>
      </div>
    );
  }

  const bankList = banks || [];

  return (
    <div className="mt-2 pb-6">
      <p className="mb-3 text-sm font-medium text-slate-700">
        Банкны аппликейшнээр төлөх
      </p>
      {bankList.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">
          Банкны жагсаалт хоосон байна
        </p>
      ) : (
        <div className="grid max-h-[min(52vh,420px)] grid-cols-3 gap-2 overflow-y-auto pr-1 sm:gap-3">
          {bankList.map((bank, index) => (
            <button
              type="button"
              key={`${bank.name}-${index}`}
              onClick={() => navigate(bank.link)}
              className="flex min-h-[80px] flex-col items-center justify-center rounded-xl border border-slate-100 bg-white p-2 shadow-sm transition hover:border-smartCyan hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-smartCyan active:scale-[0.98] sm:min-h-[88px] sm:p-3"
            >
              <img
                src={bank.logo}
                alt={bank.name}
                className="h-10 w-10 rounded-lg object-contain sm:h-12 sm:w-12 sm:rounded-xl"
                width="48"
                height="48"
              />
              <span className="mt-2 line-clamp-2 text-center text-xs leading-tight text-slate-600">
                {bank.description || bank.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QpayBankList;
